import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Users, Check, X, Download } from 'lucide-react';
import { toast } from 'sonner';

const ShiftPreferencesAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [weekStartDate, setWeekStartDate] = useState('');
  const [overview, setOverview] = useState(null);
  const [preferences, setPreferences] = useState([]);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      // Get next Monday
      const today = new Date();
      const daysUntilMonday = (7 - today.getDay() + 1) % 7 || 7;
      const nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      const weekStart = nextMonday.toISOString().split('T')[0];
      
      setWeekStartDate(weekStart);
      
      const [overviewRes, prefsRes] = await Promise.all([
        axios.get(`/admin/shift-preferences/overview/${weekStart}`),
        axios.get(`/admin/shift-preferences/${weekStart}`)
      ]);
      
      setOverview(overviewRes.data);
      setPreferences(prefsRes.data);
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Fehler beim Laden der Schichtwünsche');
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'long' });
  };

  const getFormattedDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const handleExport = () => {
    if (!overview) return;

    // Create CSV
    const headers = ['Datum', 'Tag', 'Verfügbar', 'Nicht verfügbar', 'Früh', 'Mittag', 'Spät'];
    const rows = Object.entries(overview.overview).map(([date, data]) => [
      date,
      getDayName(date),
      data.available.length,
      data.not_available.length,
      data.shifts.morning.length,
      data.shifts.afternoon.length,
      data.shifts.evening.length
    ]);

    const csv = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `schichtwuensche_${weekStartDate}.csv`;
    link.click();
    
    toast.success('CSV-Datei heruntergeladen');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const dates = overview ? Object.keys(overview.overview).sort() : [];

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white flex items-center gap-3">
            <Calendar className="h-10 w-10" />
            Schichtwünsche Übersicht
          </h1>
          <p className="text-zinc-400 mt-2">
            Woche vom {getFormattedDate(dates[0])} bis {getFormattedDate(dates[6])}
          </p>
        </div>

        <Button onClick={handleExport} size="lg">
          <Download className="mr-2 h-5 w-5" />
          CSV Export
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Eingereichte Wünsche</p>
                <p className="text-2xl font-bold text-white">{overview?.total_submissions || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Mitarbeiter insgesamt</p>
                <p className="text-2xl font-bold text-white">{preferences.length}</p>
              </div>
              <Users className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Woche</p>
                <p className="text-sm font-semibold text-white">
                  {dates[0] && getFormattedDate(dates[0])} - {dates[6] && getFormattedDate(dates[6])}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Overview */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-white">Tagesübersicht</h2>
        
        {dates.map(date => {
          const dayData = overview.overview[date];
          const totalAvailable = dayData.available.length;
          const totalNotAvailable = dayData.not_available.length;
          const coveragePercent = totalAvailable > 0 
            ? Math.round((totalAvailable / (totalAvailable + totalNotAvailable)) * 100) 
            : 0;

          return (
            <Card 
              key={date}
              className={`${
                coveragePercent >= 70 ? 'bg-green-900/20 border-green-700' :
                coveragePercent >= 40 ? 'bg-yellow-900/20 border-yellow-700' :
                'bg-red-900/20 border-red-700'
              }`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-3">
                      {getDayName(date)}
                      <Badge className={
                        coveragePercent >= 70 ? 'bg-green-900/30 text-green-400 border-green-700' :
                        coveragePercent >= 40 ? 'bg-yellow-900/30 text-yellow-400 border-yellow-700' :
                        'bg-red-900/30 text-red-400 border-red-700'
                      }>
                        {coveragePercent}% Verfügbar
                      </Badge>
                    </CardTitle>
                    <CardDescription>{getFormattedDate(date)}</CardDescription>
                  </div>

                  <div className="flex gap-3">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-400">{totalAvailable}</p>
                      <p className="text-xs text-zinc-400">Verfügbar</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-400">{totalNotAvailable}</p>
                      <p className="text-xs text-zinc-400">Nicht verfügbar</p>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {/* Shift Distribution */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-zinc-900/50 p-3 rounded-lg text-center">
                    <p className="text-sm text-zinc-400">Egal</p>
                    <p className="text-xl font-bold text-white">{dayData.shifts.any.length}</p>
                  </div>
                  <div className="bg-zinc-900/50 p-3 rounded-lg text-center">
                    <p className="text-sm text-zinc-400">Früh</p>
                    <p className="text-xl font-bold text-white">{dayData.shifts.morning.length}</p>
                  </div>
                  <div className="bg-zinc-900/50 p-3 rounded-lg text-center">
                    <p className="text-sm text-zinc-400">Mittag</p>
                    <p className="text-xl font-bold text-white">{dayData.shifts.afternoon.length}</p>
                  </div>
                  <div className="bg-zinc-900/50 p-3 rounded-lg text-center">
                    <p className="text-sm text-zinc-400">Spät</p>
                    <p className="text-xl font-bold text-white">{dayData.shifts.evening.length}</p>
                  </div>
                </div>

                {/* Available Staff */}
                {totalAvailable > 0 && (
                  <details className="mb-3">
                    <summary className="cursor-pointer text-green-400 hover:text-green-300 flex items-center gap-2 mb-2">
                      <Check className="h-4 w-4" />
                      Verfügbare Mitarbeiter ({totalAvailable})
                    </summary>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-6">
                      {dayData.available.map(emp => (
                        <div key={emp.user_id} className="bg-green-900/20 p-2 rounded text-sm">
                          <p className="text-white font-medium">{emp.user_name}</p>
                          {emp.notes && <p className="text-xs text-zinc-400">{emp.notes}</p>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {/* Not Available Staff */}
                {totalNotAvailable > 0 && (
                  <details>
                    <summary className="cursor-pointer text-red-400 hover:text-red-300 flex items-center gap-2">
                      <X className="h-4 w-4" />
                      Nicht verfügbare Mitarbeiter ({totalNotAvailable})
                    </summary>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-6 mt-2">
                      {dayData.not_available.map(emp => (
                        <div key={emp.user_id} className="bg-red-900/20 p-2 rounded text-sm">
                          <p className="text-white font-medium">{emp.user_name}</p>
                          {emp.notes && <p className="text-xs text-zinc-400">{emp.notes}</p>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No submissions */}
      {preferences.length === 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-12 text-center">
            <Calendar className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Keine Schichtwünsche eingereicht</h3>
            <p className="text-zinc-400">
              Es wurden noch keine Schichtwünsche für diese Woche eingereicht
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ShiftPreferencesAdmin;
